import { CheckCircle, XCircle, Ban } from 'lucide-react';
import { AIRetestProgress } from '../types/evaluation';

interface RetestProgressBannerProps {
  progress: AIRetestProgress | null;
  showCompletedBanner: boolean;
  onDismissCompleted: () => void;
  onCancel?: () => void;
  isCancelling?: boolean;
}

export const RetestProgressBanner = ({
  progress,
  showCompletedBanner,
  onDismissCompleted,
  onCancel,
  isCancelling,
}: RetestProgressBannerProps) => {
  if (!progress && !showCompletedBanner) return null;

  // Completed banner (auto-dismisses)
  if (showCompletedBanner && progress?.status === 'Completed') {
    return (
      <div
        data-component="RetestProgressBanner"
        data-flow="completed"
        className="mx-6 mt-6 mb-0 rounded-lg bg-green-600 text-white px-6 py-4 flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm font-medium">AI Evaluation completed</span>
        </div>
        <button
          onClick={onDismissCompleted}
          className="text-green-200 hover:text-white text-sm"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    );
  }

  // Cancelled banner
  if (progress?.status === 'Cancelled') {
    return (
      <div
        data-component="RetestProgressBanner"
        data-flow="cancelled"
        className="mx-6 mt-6 mb-0 rounded-lg bg-yellow-600 text-white px-6 py-4"
      >
        <div className="flex items-center gap-3">
          <Ban className="w-5 h-5 flex-shrink-0" />
          <div>
            <span className="text-sm font-medium">AI Evaluation cancelled</span>
            <span className="text-sm text-yellow-200 ml-2">
              — {progress.completedPrompts}/{progress.totalPrompts} prompts completed
              {progress.failedPrompts > 0 && ` (${progress.failedPrompts} failed)`}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Failed banner
  if (progress?.status === 'Failed') {
    return (
      <div
        data-component="RetestProgressBanner"
        data-flow="failed"
        className="mx-6 mt-6 mb-0 rounded-lg bg-red-600 text-white px-6 py-4"
      >
        <div className="flex items-center gap-3">
          <XCircle className="w-5 h-5 flex-shrink-0" />
          <div>
            <span className="text-sm font-medium">AI Evaluation failed</span>
            <span className="text-sm text-red-200 ml-2">
              — {progress.completedPrompts}/{progress.totalPrompts} prompts completed
              {progress.failedPrompts > 0 && ` (${progress.failedPrompts} failed)`}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Running banner with progress bar
  if (progress?.status === 'Running') {
    const percentage =
      progress.totalPrompts > 0
        ? (progress.completedPrompts / progress.totalPrompts) * 100
        : 0;

    return (
      <div
        data-component="RetestProgressBanner"
        data-flow="running"
        className="mx-6 mt-6 mb-0 rounded-lg bg-blue-600 text-white px-6 py-4"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent flex-shrink-0" />
            <span className="text-sm font-medium">
              AI Evaluation in progress: {progress.completedPrompts}/{progress.totalPrompts} prompts completed
              {progress.failedPrompts > 0 && (
                <span className="text-blue-200"> ({progress.failedPrompts} failed)</span>
              )}
            </span>
          </div>
          {onCancel && (
            <button
              onClick={onCancel}
              disabled={isCancelling}
              className="text-blue-200 hover:text-white text-sm font-medium px-3 py-1 rounded border border-blue-400 hover:border-white disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Cancel evaluation"
            >
              {isCancelling ? 'Cancelling...' : 'Cancel'}
            </button>
          )}
        </div>
        <div className="w-full bg-blue-800 rounded-full h-2 overflow-hidden">
          <div
            className="bg-white h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  }

  // Scoring in progress banner
  if (progress?.status === 'ScoringInProgress') {
    const percentage =
      progress.totalScoringRequests > 0
        ? (progress.completedScoringRequests / progress.totalScoringRequests) * 100
        : 0;

    return (
      <div
        data-component="RetestProgressBanner"
        data-flow="scoring"
        className="mx-6 mt-6 mb-0 rounded-lg bg-indigo-600 text-white px-6 py-4"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent flex-shrink-0" />
            <span className="text-sm font-medium">
              Scoring in progress: {progress.completedScoringRequests}/{progress.totalScoringRequests} scores completed
            </span>
          </div>
          {onCancel && (
            <button
              onClick={onCancel}
              disabled={isCancelling}
              className="text-indigo-200 hover:text-white text-sm font-medium px-3 py-1 rounded border border-indigo-400 hover:border-white disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Cancel evaluation"
            >
              {isCancelling ? 'Cancelling...' : 'Cancel'}
            </button>
          )}
        </div>
        <div className="w-full bg-indigo-800 rounded-full h-2 overflow-hidden">
          <div
            className="bg-white h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  }

  return null;
};
